import { useTranslation } from 'react-i18next';

import { useMediaPreview } from '@/components/question/forms/shared/useMediaPreview';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import CoverImageInput from '@/components/shared/CoverImageInput';
import { TextInput } from '@/components/ui/daisy';
import {
  MAX_QUIZ_ANSWERS_FILE_SIZE,
  QUIZ_ANSWERS_ACCEPTED_IMAGE_TYPES,
} from '@/constants';
import { QuestionCreateDTO } from '@/models/Answer';
import { detectMediaKind } from '@/utils/media';

type QuestionColumnItemMediaProps = {
  item: QuestionCreateDTO;
  onUpdateMedia: (file: File | string, altText?: string) => void;
};

export default function QuestionColumnItemMedia({
  item,
  onUpdateMedia,
}: QuestionColumnItemMediaProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.questionsTab.createQuestionModal.questionForm',
  });

  const previewUrl = useMediaPreview(item.mediaFile, item.mediaLink);
  const currentAltText = item.altText ?? '';

  const kind = detectMediaKind(item.mediaFile) ?? detectMediaKind(previewUrl) ?? 'image';

  const handleImageChange = (url: string) => {
    if (!url) onUpdateMedia('', currentAltText);
  };

  const handleFileSelect = (file: File) => {
    onUpdateMedia(file, currentAltText);
  };

  const handleAltTextChange = (altText: string) => {
    onUpdateMedia(item.mediaFile ?? '', altText);
  };

  return (
    <div className="flex items-end gap-3 w-full">
      <ConditionalRenderer condition={kind === 'image'}>
        <CoverImageInput
          value={previewUrl ?? undefined}
          onChange={handleImageChange}
          onFileSelect={handleFileSelect}
          accept={QUIZ_ANSWERS_ACCEPTED_IMAGE_TYPES}
          limit={MAX_QUIZ_ANSWERS_FILE_SIZE}
          hideTrashButton
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={kind === 'audio'}>
        <audio
          src={previewUrl ?? undefined}
          controls
          preload="metadata"
          className="h-10"
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={kind === 'video'}>
        <video
          src={previewUrl ?? undefined}
          controls
          preload="metadata"
          className="h-24 rounded object-contain"
        />
      </ConditionalRenderer>
      <TextInput
        value={currentAltText}
        placeholder={t('altTextPlaceholder')}
        className="w-full"
        onChange={e => handleAltTextChange(e.target.value)}
      />
    </div>
  );
}
